/**
 * Demo Permissions Display Component
 * Shows the current user's permissions and access level for demonstration purposes
 */

import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './DemoPermissionsDisplay.css';

export const DemoPermissionsDisplay: React.FC = () => {
  const currentUser = useQuery(api.users.getUserInfo);
  const demoStatus = useQuery(api.users.getDemoStatus);

  if (!currentUser || !demoStatus) {
    return (
      <div className="permissions-display loading">
        <p>Loading permissions...</p>
      </div>
    );
  }

  // Handle case where user doesn't have permissions yet
  if (!currentUser.permissions) {
    return (
      <div className="permissions-display loading">
        <p>Setting up permissions...</p>
      </div>
    );
  }

  const roleDescriptions = {
    processor: "Basic access level - Can view and create claims, upload documents",
    adjuster: "Mid-level access - Can investigate claims, assign cases, access policies", 
    manager: "Senior access - Can approve/deny claims, generate reports, manage team",
    admin: "Full system access - User management, audit logs, system configuration"
  };

  return (
    <div className="permissions-display">
      <div className="permissions-card">
        <div className="card-header">
          <h3>Current Access Level</h3>
          <span className={`role-badge ${currentUser.role}`}>
            {currentUser.role.toUpperCase()}
          </span>
        </div>
        
        <div className="card-content">
          <div className="role-description">
            <p>{roleDescriptions[currentUser.role as keyof typeof roleDescriptions]}</p>
          </div>

          <div className="permissions-grid">
            <div className="permissions-section">
              <h4>Accessible Sections</h4>
              <div className="permissions-list">
                {currentUser.permissions.sections.map((section: string) => (
                  <span key={section} className="permission-tag section">
                    {section.charAt(0).toUpperCase() + section.slice(1)}
                  </span>
                ))}
              </div>
            </div>

            <div className="permissions-section">
              <h4>Available Actions</h4>
              <div className="permissions-list">
                {currentUser.permissions.actions.map((action: string) => (
                  <span key={action} className="permission-tag action">
                    {action.charAt(0).toUpperCase() + action.slice(1)}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="demo-info">
            <div className="demo-status">
              <span className="demo-indicator">🎭</span>
              <span>Demo Mode Active - Switch roles to explore different access levels</span>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default DemoPermissionsDisplay;